import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Drawer,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  AlertsContext,
  DistaContent,
  DistaPageHeaderProps,
} from "@sista/dista-ui";
import { SistaPageLayout } from "@sista/fe-commons";
import { IdmClientContext } from "@sista/idm-client";
import {
  LoadingGuard,
  ShowAccessDeniedMsg,
  SistaId,
  useSafeSistaAsync,
} from "@sista/library-stasi";
import {
  GridRowSelectionModel,
  OrganizationProjectList,
  SkeletonProjectData,
} from "@sista/service-project-skeleton";
import {
  SistaSpravaUkoluList,
  SistaTaskBanner,
  SistaTaskPresent,
  SistaWorkflowProvider,
} from "@sista/service-workflow-ui";
import { SistaChip } from "@sista/sista-chip";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useRejstrikClient } from "../../../api/utils/useRejstrikClient";
import { useLocalNavigation } from "../../../hooks/useLocalNavigation";
import { OrganizationChangeNoticeDetailContent } from "../../content/organization/OrganizationChangeNoticeDetailContent";

type ApiProject = {
  id?: string;
  roCfpId?: string;
  roNotFundedId?: string;
  roContractConclusionId?: string;
  roImplementationId?: string;
  roEndedId?: string;
};

type ProjectsPanelProps = {
  organizationId: string;
  providerId: string;
  authKey: string;
  selectedProjects: SkeletonProjectData[];
  initialSelectionIds: GridRowSelectionModel;
  onSelectionChange: (projects: SkeletonProjectData[]) => void;
  onContinue: () => void;
  isSaving: boolean;
  onAddAfterEnd: () => void;
  isAddingAfterEnd: boolean;
  noticeStatus: string;
};

const TaskPresenceDetector = ({
  onPresent,
}: {
  onPresent: (v: boolean) => void;
}): null => {
  useEffect(() => {
    onPresent(true);
    return () => onPresent(false);
  }, [onPresent]);
  return null;
};

const ProjectsPanel = ({
  organizationId,
  providerId,
  authKey,
  selectedProjects,
  initialSelectionIds,
  onSelectionChange,
  onContinue,
  isSaving,
  onAddAfterEnd,
  isAddingAfterEnd,
  noticeStatus,
}: ProjectsPanelProps) => {
  const { t } = useTranslation("ucet");
  const [canInteract, setCanInteract] = useState(false);
  const handlePresent = useCallback((v: boolean) => setCanInteract(v), []);
  const hasInteracted = useRef(false);

  const initialIds = initialSelectionIds as string[];
  const isAfterEnd = !canInteract && noticeStatus === "ACKNOWLEDGED";

  const rowSelectionModel: GridRowSelectionModel = isAfterEnd
    ? [...new Set([...initialIds, ...selectedProjects.map((p) => p.id)])]
    : hasInteracted.current
    ? selectedProjects.map((p) => p.id)
    : initialSelectionIds;

  const activeChipIds: string[] =
    canInteract && hasInteracted.current
      ? selectedProjects.map((p) => p.id)
      : initialIds;

  const newProjects: SkeletonProjectData[] =
    isAfterEnd && hasInteracted.current
      ? selectedProjects.filter((p) => !initialIds.includes(p.id))
      : [];

  const showAddAfterEnd = isAfterEnd && newProjects.length > 0;

  const lockedRowsSx =
    isAfterEnd && initialIds.length > 0
      ? Object.fromEntries(
          initialIds.map((id) => [
            `& .MuiDataGrid-row[data-id="${id}"] .MuiCheckbox-root`,
            { color: "#bdbdbd", pointerEvents: "none" },
          ])
        )
      : {};

  return (
    <>
      <Box sx={{ mb: 1 }}>
        {activeChipIds.length > 0 && (
          <>
            <Typography variant="h6">
              {t(
                "ucet.organizationChangeNotice.provider.affectedProjects",
                "Vybrané projekty"
              )}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
              {activeChipIds.map((id) => (
                <SistaChip
                  key={id}
                  authToken={authKey}
                  urn={"urn:sista:projects:" + id}
                  variant="tiny"
                />
              ))}
            </Stack>
          </>
        )}
        {newProjects.length > 0 && (
          <Stack sx={{ mt: activeChipIds.length > 0 ? 1.5 : 0 }}>
            <Typography variant="h6">
              {t(
                "ucet.organizationChangeNotice.provider.newProjects",
                "Nově přidané projekty"
              )}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
              {newProjects.map((p) => (
                <SistaChip
                  key={p.id}
                  authToken={authKey}
                  urn={"urn:sista:projects:" + p.id}
                  variant="tiny"
                />
              ))}
            </Stack>
            {showAddAfterEnd && (
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                <Button
                  variant="contained"
                  disabled={isAddingAfterEnd}
                  onClick={onAddAfterEnd}
                >
                  {t(
                    "ucet.organizationChangeNotice.provider.addAfterEnd",
                    "Doplnit dotčené projekty"
                  )}
                </Button>
              </Stack>
            )}
          </Stack>
        )}
      </Box>
      <Box
        sx={{
          height: "calc(100vh - 246px)",
          ...lockedRowsSx,
        }}
      >
        <OrganizationProjectList
          filter={{
            organizationId,
            providerId,
            projectState: ["200", "300", "400", "500", "600", "700"],
          }}
          displayColumns={[
            "roCfpId",
            "projectState",
            "projectSubState",
            "projectAdminId",
            "projectMainInvestigatorId",
          ]}
          checkboxSelection
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(
            _model: GridRowSelectionModel,
            selected: SkeletonProjectData[]
          ) => {
            hasInteracted.current = true;
            onSelectionChange(selected);
          }}
        />
      </Box>
      {canInteract && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2, mb: 2 }}>
          <Button variant="contained" disabled={isSaving} onClick={onContinue}>
            {t("ucet.organizationChangeNotice.provider.continue", "Pokračovat")}
          </Button>
        </Stack>
      )}
      <SistaTaskPresent>
        <TaskPresenceDetector onPresent={handlePresent} />
      </SistaTaskPresent>
    </>
  );
};

export const OrganizationChangeNoticeProviderScreen = () => {
  const { t } = useTranslation("ucet");
  const { organizationId, changeNoticeId } = useParams();
  const localNavigationConfig = useLocalNavigation();
  const rej = useRejstrikClient();
  const { loggedAccount } = useContext(IdmClientContext);
  const { showSuccess } = useContext(AlertsContext);

  const providerId = loggedAccount?.providerId ?? "tacr";
  const isProvider = useMemo(
    () => loggedAccount?.activeProfile?.type === "PROVIDER",
    [loggedAccount?.activeProfile?.type]
  );

  const [getOrganizationCall, getOrganizationState] = useSafeSistaAsync(
    rej.getOrganizationById
  );
  const [getNoticeCall, getNoticeState] = useSafeSistaAsync(
    rej.getOrganizationChangeNoticeDetail
  );
  const [getAffectedProjectsCall, getAffectedProjectsState] = useSafeSistaAsync(
    rej.getAffectedProjectsByProvider
  );
  const [updateAffectedProjectsCall, updateAffectedProjectsState] =
    useSafeSistaAsync(rej.updateAffectedProjectsByProvider);
  const [addAffectedProjectsAfterEndCall, addAffectedProjectsAfterEndState] =
    useSafeSistaAsync(rej.addAffectedProjectsByProviderAfterEnd);
  const [showWorkflowDialog, setShowWorkflowDialog] = useState(false);

  const [selectedProjects, setSelectedProjects] = useState<
    SkeletonProjectData[]
  >([]);
  const [initialSelectionIds, setInitialSelectionIds] =
    useState<GridRowSelectionModel>([]);
  const [initialApiProjects, setInitialApiProjects] = useState<ApiProject[]>(
    []
  );
  const selectionInitialized = useRef(false);
  const hasInteracted = useRef(false);

  const taskContext = useMemo(() => {
    if (!organizationId || !changeNoticeId) return [];
    return [
      new SistaId("organizations", organizationId).asUrn(),
      new SistaId("workflows", "organization_change_notice").asUrn(),
      new SistaId("organizationChangeNotice", changeNoticeId).asUrn(),
    ];
  }, [organizationId, changeNoticeId]);

  useEffect(() => {
    if (!organizationId || !changeNoticeId || !isProvider) return;
    getOrganizationCall({ organizationId });
    getNoticeCall({ organizationId, changeNoticeId });
    getAffectedProjectsCall({ organizationId, changeNoticeId, providerId });
  }, [
    organizationId,
    changeNoticeId,
    isProvider,
    providerId,
    getOrganizationCall,
    getNoticeCall,
    getAffectedProjectsCall,
  ]);

  const organization = useMemo(
    () => getOrganizationState.value,
    [getOrganizationState.value]
  );
  const notice = useMemo(() => getNoticeState.value, [getNoticeState.value]);

  useEffect(() => {
    const affectedProjects = getAffectedProjectsState.value;
    if (!affectedProjects || selectionInitialized.current) return;
    const apiProjects = affectedProjects.projects ?? [];
    setInitialApiProjects(apiProjects);
    setInitialSelectionIds(
      apiProjects.map((p) => p.id).filter((id): id is string => !!id)
    );
    selectionInitialized.current = true;
  }, [getAffectedProjectsState.value]);

  const handleContinue = async () => {
    if (!organizationId || !changeNoticeId) return;
    const projects = hasInteracted.current
      ? selectedProjects.map((p) => ({
          id: p.id,
          roCfpId: p.roCfpId,
          roNotFundedId: p.roNotFundedId ?? undefined,
          roContractConclusionId: p.roContractConclusionId ?? undefined,
          roImplementationId: p.roImplementationId ?? undefined,
          roEndedId: p.roEndedId ?? undefined,
        }))
      : initialApiProjects;
    const result = await updateAffectedProjectsCall({
      organizationId,
      changeNoticeId,
      providerId,
      projects,
    });
    if (result) {
      setShowWorkflowDialog(true);
    }
  };

  const handleAddAfterEnd = async () => {
    if (!organizationId || !changeNoticeId) return;
    const initialIds = new Set(
      initialApiProjects.map((p) => p.id).filter(Boolean)
    );
    const projects = selectedProjects
      .filter((p) => !initialIds.has(p.id))
      .map((p) => ({
        id: p.id,
        providerId: p.providerId,
        roCfpId: p.roCfpId,
        roNotFundedId: p.roNotFundedId ?? undefined,
        roContractConclusionId: p.roContractConclusionId ?? undefined,
        roImplementationId: p.roImplementationId ?? undefined,
        roEndedId: p.roEndedId ?? undefined,
      }));
    const result = await addAffectedProjectsAfterEndCall({
      organizationId,
      changeNoticeId,
      providerId,
      projects,
    });
    if (result) {
      showSuccess(
        t(
          "ucet.organizationChangeNotice.provider.addAfterEndSuccess",
          "Dotčené projekty byly úspěšně doplněny."
        )
      );
      selectionInitialized.current = false;
      hasInteracted.current = false;
      setSelectedProjects([]);
      getAffectedProjectsCall({ organizationId, changeNoticeId, providerId });
    }
  };

  const headerConfig: DistaPageHeaderProps = {
    breadcrumbs: [
      {
        title: t("ucet.menu.breadcrumbs.home", "Domů"),
        action: () => (window.location.href = "/ucet/"),
      },
      {
        title: organization?.displayName ?? "...",
      },
      {
        title: t(
          "ucet.organizationChangeNotice.detail.breadcrumb",
          "Oznámení změny"
        ),
      },
    ],
    pageTitle: organization?.displayName,
  };

  return isProvider ? (
    <SistaPageLayout localNavigationConfig={localNavigationConfig}>
      <DistaContent headerConfig={headerConfig} fullWidth={true}>
        <LoadingGuard state={getOrganizationState}>
          <LoadingGuard state={getNoticeState}>
            <LoadingGuard state={getAffectedProjectsState}>
              {organization && notice && (
                <SistaWorkflowProvider
                  loggedAuthKey={loggedAccount.authKey}
                  workflowContext={taskContext}
                >
                  <SistaTaskBanner />
                  <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
                    {t(
                      "ucet.organizationChangeNotice.provider.projectImpact",
                      "Posouzení dopadu na projekty"
                    )}
                  </Typography>
                  <Grid container spacing={4} sx={{ mt: 0 }}>
                    <Grid item xs={12} md={5}>
                      <OrganizationChangeNoticeDetailContent
                        organization={organization}
                        notice={notice}
                        onBack={null}
                        hideTasks={true}
                      />
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <ProjectsPanel
                        organizationId={organizationId}
                        providerId={providerId}
                        authKey={loggedAccount.authKey}
                        selectedProjects={selectedProjects}
                        initialSelectionIds={initialSelectionIds}
                        onSelectionChange={(projects) => {
                          hasInteracted.current = true;
                          setSelectedProjects(projects);
                        }}
                        onContinue={handleContinue}
                        isSaving={updateAffectedProjectsState.pending}
                        onAddAfterEnd={handleAddAfterEnd}
                        isAddingAfterEnd={
                          addAffectedProjectsAfterEndState.pending
                        }
                        noticeStatus={notice.status}
                      />
                    </Grid>
                  </Grid>
                </SistaWorkflowProvider>
              )}
            </LoadingGuard>
          </LoadingGuard>
        </LoadingGuard>
      </DistaContent>

      <Drawer
        anchor="right"
        open={showWorkflowDialog}
        onClose={() => setShowWorkflowDialog(false)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 520 }, p: 3 } }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">
            {t(
              "ucet.organizationChangeNotice.provider.workflowTitle",
              "Řešení procesu"
            )}
          </Typography>
          <IconButton onClick={() => setShowWorkflowDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
        <SistaSpravaUkoluList
          loggedAccount={loggedAccount}
          loggedAuthKey={loggedAccount.authKey}
          tasksContext={taskContext}
          spravaUkoluRoot={"/sprava-ukolu"}
          onSuccess={() => {
            if (!organizationId || !changeNoticeId) return;
            selectionInitialized.current = false;
            hasInteracted.current = false;
            setSelectedProjects([]);
            getNoticeCall({ organizationId, changeNoticeId });
            getAffectedProjectsCall({
              organizationId,
              changeNoticeId,
              providerId,
            });
          }}
        />
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={() => setShowWorkflowDialog(false)}
          >
            {t("ucet.organizationChangeNotice.close", "Zavřít")}
          </Button>
        </Stack>
      </Drawer>
    </SistaPageLayout>
  ) : (
    <SistaPageLayout localNavigationConfig={localNavigationConfig}>
      <DistaContent headerConfig={headerConfig}>
        <ShowAccessDeniedMsg />
      </DistaContent>
    </SistaPageLayout>
  );
};
